<template>
	<div role="region" class="panel-wrapper hidden-print" :id="id" aria-label="filters section">
		<div class="filters-section">
			<div class="container">
				<div class="row row-xs-bottom">
					<div :class="itemCssClass" v-for="filter in nonToggleFilters" :key="filter.id">

						<!-- Multiselect -->
						<div v-if="filter.control === 'multiselect'">
							<Multiselect
								:id="filter.id"
								:ref="filter.id"
								:groups="filter.groups"
								:filterName="filter.id"
								:placeholder="filter.placeholder"
								:required="filter.required"
								:externalData="{ value: filter.filterType }"
								@change="updateSelectedFilters"
							/>
						</div>

						<!-- Datepicker -->
						<DateSelect
							v-else-if="filter.control === 'dateSelect'"
							:filterName="filter.id"
							:ref="filter.id"
							:placeholder="filter.placeholder"
							:initialDate="filter.value"
							:externalData="{ value: filter.filterType }"
							:daysMin="daysMin"
							:daysMax="daysMax"
							@change="updateSelectedFilters"
						/>

						<!-- (Single) Select -->
						<SingleSelect
							v-else-if="filter.control === 'singleSelect'"
							:ref="filter.id"
							:choices="filter.choices"
							:filterName="filter.id"
							:placeholder="filter.placeholder"
							:required="filter.required"
							:externalData="{ value: filter.filterType }"
							@change="updateSelectedFilters"
						/>

					</div>
				</div>
				<div>
					<span v-for="filter in toggleFilters" :key="filter.id">

						<!-- Checkbox -->
						<span v-if="filter.control === 'toggle'">
							<label class="c-input c-checkbox m-t-sm m-b-0" :for="filter.id" data-testid="searchFiltersLabel">
								<input
									type="checkbox"
									:checked="filter.checked"
									:name="filter.id"
									:id="filter.id"
									@change="updateSelectedFilters({ value: !filter.value }, { value: filter.filterType })" /><span class="c-indicator"></span>{{ filter.placeholder }}
							</label>
						</span>

					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import { StateService } from '@/services/state.service';
import Multiselect from '@/components/schreg-common/Multiselect.vue';
import DateSelect from '@/components/schreg-common/DateSelect.vue';
import SingleSelect from '@/components/schreg-common/SingleSelect.vue';
import { FilteringService } from '@/services/filtering.service';
import { FrameworkService } from '@/services/framework.service';

export default defineComponent({
	name: 'SearchFilters',
	components: {
		Multiselect,
		DateSelect,
		SingleSelect
	},
	props: {
		filterPage: {
			type: String,
			required: true
		},
		facets: {
			type: Array,
			default: () => []
		},
		queryFilters: {
			type: Array,
			default: () => []
		},
		id: {
			type: String,
			required: true
		},
		filterTypes: {
			type: Array,
			required: true
		},
		prefix: {
			type: String,
			required: true
		},
		savedSearchName: {
			type: String,
			default: null
		},
		savedSearchFilters: {
			type: Array,
			required: true
		},
		itemCssClass: {
			type: String,
			default: 'col-md-4 col-sm-12 col-xs-12'
		}
	},
	data() {
		return {
			filters: [],
			daysMin: 0,
			daysMax: 90
		};
	},
	computed: {
		nonToggleFilters() {
			return _.filter(this.filters, function(f) { return f.control !== 'toggle'; });
		},
		toggleFilters() {
			return _.filter(this.filters, function(f) { return f.control === 'toggle'; });
		}
	},
	watch: {
		facets: function() {
			this.updateFilters();
			this.$emit('rebuildQueryFilters');
		}
	},
	mounted() {
		const daysSettings = FilteringService.getDaysSettings(this.filterPage);

		this.daysMin = daysSettings.min;
		this.daysMax = daysSettings.max;

		const _filters = [];
		for (let filterType of this.filterTypes) {
			const _queryValues = this.queryFilters
				.filter(item => {return item.type === filterType;})
				.map(item => item.value);

			let _filter = FilteringService.build(
				filterType,
				this.facets,
				this.prefix,
				_queryValues,
				this.savedSearchFilters,
				true
			);

			if (_filter) _filters.push(_filter);
		}
		this.filters = _filters;
		this.$emit('rebuildQueryFilters');
	},
	methods: {
		// Quick lookup of a rendered filter by its html Id attribute
		ref(filterId) {
			let _ref = this.$refs[filterId];
			if (_ref.length) _ref = _ref[0];
			return _ref;
		},

		// This will disable/enable all non-required filters
		setNonRequiredDisabled(value) {
			for (let filter of this.filters) {
				switch (filter.control) {
					case 'multiselect': {
						if (!filter.required) this.ref(filter.id).setForceDisabled(value);
						break;
					}
					case 'toggle': {
						let ref = document.getElementById(filter.id);
						ref.disabled = value;
						break;
					}
					case 'dateSelect': {
						// currently we won't need to disable a date picker
						break;
					}
					case 'singleSelect': {
						if (!filter.required) this.ref(filter.id).setForceDisabled(value);
						break;
					}
					default: break;
				}
			}
		},
		// This method is called when the incoming facets are changed.
		// It will re-render each filter, using values from the queryString to override defaults
		updateFilters() {
			for (let filter of this.filters) {
				const newFilterData = FilteringService.build(
					filter.filterType,
					this.facets,
					this.prefix,
					_.map(_.filter(this.queryFilters, item => {return item.type === filter.filterType;}), item => item.value), // Filter by type before map (like in mounted)
					this.savedSearchFilters,
					false
				);
				switch (filter.control) {
					case 'multiselect': {
						this.ref(filter.id).setGroups(newFilterData.groups, !!this.savedSearchName);
						break;
					}
					case 'toggle': {
						if (this.queryFilters.length) {
							let ref = document.getElementById(filter.id);
							ref.checked = newFilterData.checked;
						}
						break;
					}
					case 'dateSelect': {
						this.ref(filter.id).setValue(newFilterData.value);
						break;
					}
					case 'singleSelect': {
						this.ref(filter.id).setChoices(newFilterData.choices);
						break;
					}
					default: break;
				}
			}
		},

		// This method is called when the filter value changes
		updateSelectedFilters: _.debounce(function(itemClicked, filterType) {
			const updateSource = {
				type: filterType.value,
				value: itemClicked ? itemClicked.value : null
			};

			const updatedFilters = FilteringService.searchDefault(this.filterPage, updateSource);

			for (let filter of this.filters) {
				switch (filter.control) {
					case 'multiselect': {
						const _values = this.ref(filter.id).getSelectedValues();
						const _chiclets = this.ref(filter.id).getSelectedObjects();
						if (_values.length > 0) {
							FilteringService.search(filter.id, updatedFilters, _values, this.prefix);
						}
						if (_chiclets.length) {
							for (let chiclet of _chiclets) {
								updatedFilters.chiclets.push({
									name: chiclet.nameChiclet || chiclet.name,
									value: chiclet.value,
									type: filter.filterType,
									disabled: filter.filterType === 'location' && chiclet.value === StateService.getLocationName()
								});
							}
						}
						break;
					}
					case 'toggle': {
						const ref = document.getElementById(filter.id);
						FilteringService.search(filter.id, updatedFilters, ref.checked, this.prefix);
						if (ref.checked) {
							updatedFilters.chiclets.push({
								name: FilteringService.getChicletName(filter.filterType),
								value: true,
								type: filter.filterType
							});
						}
						break;
					}
					case 'dateSelect': {
						const _value = this.ref(filter.id).getValue();
						FilteringService.search(filter.id, updatedFilters, _value, this.prefix);
						break;
					}
					case 'singleSelect': {
						const _value = this.ref(filter.id).getSelectedValue();
						if (_value) {
							FilteringService.search(filter.id, updatedFilters, _value, this.prefix);
						}
						break;
					}
					default: break;
				}
			}
			this.$emit('changed', updatedFilters, updateSource);
		}, 100),

		selectFilterValue(filterType, filterValue, triggerUpdate) {
			const filter = _.find(this.filters, item => {
				return item.filterType === filterType;
			});
			if (!filter) return;

			switch (filter.control) {
				case 'multiselect': {
					this.ref(filter.id).setSelected(filterValue, triggerUpdate);
					break;
				}
				case 'toggle': {
					document.getElementById(filter.id).checked = true;
					if (triggerUpdate) this.updateSelectedFilters({ value: true }, { value: filterType });
					break;
				}
				case 'dateSelect': {
					// No need to update selected filters, this will never be called
					break;
				}
				case 'singleSelect': {
					this.ref(filter.id).setSelected(filterValue, triggerUpdate);
					break;
				}
				default: break;
			}
		},

		enableDisableFilterIndex(filterType, index, enabled) {
			const filter = _.find(this.filters, item => {
				return item.filterType === filterType;
			});
			if (!filter) return;

			switch (filter.control) {
				case 'multiselect': {
					// No need to update selected filters, this will never be called (only implemented for singleSelect)
					break;
				}
				case 'toggle': {
					// No need to update selected filters, this will never be called (only implemented for singleSelect)
					break;
				}
				case 'dateSelect': {
					// No need to update selected filters, this will never be called (only implemented for singleSelect)
					break;
				}
				case 'singleSelect': {
					this.ref(filter.id).setEnabledDisabledIndex(index, enabled);
					break;
				}
				default: break;
			}
		},

		deselectFilterValue(filterType, filterValue) {
			const filter = _.find(this.filters, item => {
				return item.filterType === filterType;
			});
			if (!filter) return;

			switch (filter.control) {
				case 'multiselect': {
					if (filter.filterType === 'member' && filterValue.indexOf(':') > -1) {
						let memberData = FrameworkService.data('profile.members');
						_.forEach(memberData, function(a) {
							if (filterValue.split(':')[1] === a.firstName) {
								filterValue = 'member:' + a.memberId;
							} else if (a.memberType === filterValue.toString().split(' ')[1] && filterValue.split(' ')[0].split(':')[1] === a.firstName) {
								filterValue = 'member:' + a.memberId;
							}
						});
						this.ref(filter.id).setUnselected(filterValue);
					} else {
						this.ref(filter.id).setUnselected(filterValue); // this event will emit an update that will propagate back
					}

					this.ref(filter.id).setUnselected(filterValue); // This event will emit an update that will propagate back
					break;
				}
				case 'toggle': {
					document.getElementById(filter.id).checked = false;
					this.updateSelectedFilters({ value: false }, { value: filterType });
					break;
				}
				case 'dateSelect': {
					// No need to update selected filters, this will never be called
					break;
				}
				case 'singleSelect': {
					this.ref(filter.id).setUnselected();
					break;
				}
				default: break;
			}
		},

		validateQueryValues(queryFilters) {
			function _validateFilter(filter, check) {
				const found = {
					item: null,
					valid: false
				};
				switch (filter.control) {
					case 'multiselect': {
						for (const group of filter.groups) {
							found.item = _.find(group.choices, item => {
								return item.value === check.value;
							});
							if (found.item) return true;
						}
						if (!found.item && filter.filterType === 'member') {
							for (const group of filter.groups) {
								found.item = _.find(group.choices, item => {
									_.forEach(FrameworkService.data('profile.members'), function(r) {
										if (typeof check.value === 'string' && r.memberType === check.value.split(' ')[1] && r.firstName === check.value.split(' ')[0].split(':')[1]) {
											check.value = 'member:' + r.memberId;
										}
									});
									return item.value === check.value;
								});
								if (found.item) return true;
							}
						}
						found.valid = typeof found.item === 'object';
						break;
					}
					case 'toggle': {
						if (filter.filterType === check.type) found.valid = true;
						break;
					}
					case 'dateSelect': {
						// TODO: Make sure the date in the query string is a proper format (currently always reported as valid)
						found.valid = true;
						break;
					}
					case 'singleSelect': {
						found.item = _.find(filter.choices, item => {
							return item.value === check.value;
						});
						found.valid = typeof found.item === 'object';
						break;
					}
					default: break;
				}
				return found.valid;
			}

			const invalidFilters = [];

			const checkingFilters = _.map(queryFilters, item => {
				if (item.type === 'member') {
					let memberData = FrameworkService.data('profile.members');
					_.forEach(memberData, function(a) {
						if (a.firstName === item.value.split(':')[1]) {
							item.value = 'member:' + a.memberId;
						}
					});
				}
				return {
					type: item.type,
					value: item.value,
					valid: false,
					query: item.query
				};
			});

			for (let check of checkingFilters) {
				if (!check.query) continue;
				for (let filter of this.filters) {
					let foundItem = _validateFilter(filter, check);
					if (foundItem) {
						check.valid = true;
						break;
					}
				}
				if (!check.valid) invalidFilters.push(check.value);
			}
			return invalidFilters;
		},

		exportSave() {
			const _export = {};
			for (let filter of this.filters) {
				switch (filter.control) {
					case 'multiselect': {
						const _values = this.ref(filter.id).getSelectedValues();
						if (_values.length > 0) {
							_export[filter.export] = _.map(_values, item => {
								// Note: We're assuming the first element is the tagPrefix, but it's possible that a filter that doesn't use a tagPrefix could have a value with a colon, which would not save a working favorite
								let [, ...rest] = item.split(':');
								rest = rest.join(':');
								return item.indexOf(':') > -1 ? rest : item;
							});
						}
						break;
					}
					case 'toggle': {
						const ref = document.getElementById(filter.id);
						if (ref.checked) {
							_export[filter.export] = true;
						}
						break;
					}
					case 'dateSelect': {
						// never export a selected date
						break;
					}
					case 'singleSelect': {
						// TODO: export single select eventually
						break;
					}
					default: break;
				}
			}
			return _export;
		},

		getQueryStringValues() {
			const _export = [];
			for (let filter of this.filters) {
				switch (filter.control) {
					case 'multiselect': {
						const _values = this.ref(filter.id).getSelectedValues();

						if (filter.filterType === 'member') {
							let memberData = FrameworkService.data('profile.members');
							let sameMemberName = _.filter(memberData, v => _.filter(memberData, v1 => v1.firstName === v.firstName).length > 1);
							for (var i=0; i < memberData.length; i++) {
								for (var j=0; j < _values.length; j++) {
									if (sameMemberName.length > 1) {
										for (var k=0; k < sameMemberName.length; k++) {
											if (sameMemberName.length > 1 && sameMemberName[k].memberId === parseInt(_values[j].split(':')[1],10)) {
												_values[j] ='member:' + sameMemberName[k].firstName + ' ' + sameMemberName[k].memberType;
											} else if (parseInt(_values[j].split(':')[1],10) === memberData[i].memberId) {
												_values[j] ='member:' + memberData[i].firstName;
											}
										}
									} else if (parseInt(_values[j].split(':')[1],10) === memberData[i].memberId) {
										_values[j] ='member:' + memberData[i].firstName;
									}
								}
							}
						}

						if (_values.length > 0) {
							_export.push({
								name: filter.filterType,
								values:  _.map(_values, item => {return item.indexOf(':') > -1 ? item.split(':')[1] : item;})
							});
						}
						break;
					}
					case 'toggle': {
						const ref = document.getElementById(filter.id);
						if (ref.checked) {
							_export.push({
								name: filter.filterType,
								values: true
							});
						}
						break;
					}
					case 'dateSelect': {
						const _value = this.ref(filter.id).getValue();
						if (_value) {
							_export.push({
								name: filter.filterType,
								values: _value
							});
						}
						break;
					}
					case 'singleSelect': {
						const _value = this.ref(filter.id).getSelectedValue();
						if (_value) {
							_export.push({
								name: filter.filterType,
								values: _value
							});
						}
						break;
					}
					default: break;
				}
			}
			return _export;
		},

		testTouch(event) {
			const el = $(event.target);
			if (el.attr('type') !== 'date') {
				event.preventDefault();
				el.attr('type', 'date');
				el.addClass('datepicker-hide');
				el.focus();
			}
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/panels.scss";
@import "styles/SearchFilters.scss";
</style>
